import { useSearchParams, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useRef, useMemo } from 'react'
import PrefixedText from 'components/common/PrefixedText/PrefixedText'
import Edit from 'components/assets/icons/Edit'
import Link from 'components/common/Link/Link'
import { pages } from 'components/RCPro/helpers/nav'
import { usePrice } from 'components/RCPro/components/Context/Context'
import api from 'helpers/api'
import style from './Oups.module.scss'
import Copy from 'components/assets/icons/Copy'

const RCProOups = () => {
  const navigate = useNavigate()
  const { state, pathname } = useLocation()
  const [ searchParams ] = useSearchParams()
  const { page } = usePrice()
  const reason = searchParams.get('reason')
  const { oups } = state?.texts || {}
  const errMessage = state?.errMessage || 'unknown issue'
  const project = pathname.split('/')[1]
  const previous = JSON.parse(localStorage.getItem(project === 'rc-pro' ? 'RCPro' : project)) || {}
  const hasFetchedRef = useRef(false);
  const backTo = {
    'estimate': 8,
    'resume': 11
  }

  const issues = useMemo(() => {
    const baseIssues = state?.issues || [];
    return reason === '401'
      ? [...baseIssues, { page, errMessage }]
      : baseIssues
  }, [state?.issues, reason, page, errMessage])

  useEffect( () => {
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true
    const sendMessage = async () => {
      try{
        const message = issues.reduce( (acc, cur, id) => acc + `${id+1}) Issue: ${cur.errMessage}, on page ${cur.page}\r\n`, '')
        await api.post({
          url: `/RCPro/issue`,
          data: { message },
          headers: { Authorization: `Bearer ${(project === 'rc-pro' ? process.env.REACT_APP_RCPRO_URL : process.env.REACT_APP_RCA_URL).split('token=')[1]}` }
        })
      } catch (error) {
        console.log('Send message error')
      }
    }

    // Got two issues in a row
    if (issues.length === 2 && reason === '401'){
      sendMessage()
    }
  }, [issues, reason, page, errMessage, project])

  const reasons = [
    {
      reason: 'conflict',
      title: oups?.conflitTitle || 'oups.conflitTitle',
      content: oups?.conflitText || 'oups.conflitText',
      backto: backTo[previous.page] ?? 2
    },
    {
      reason: 'no-company',
      title: oups?.noCompanyTitle || 'oups.noCompanyTitle',
      content: oups?.noCompanyText || 'oups.noCompanyText',
      backto: backTo[previous.page] ?? 3
    },
    {
      reason: 'registered-company',
      title: oups?.registeredCompanyTitle || 'oups.registeredCompanyTitle',
      content: oups?.registeredCompanyText || 'oups.registeredCompanyText',
      backto: backTo[previous.page] ?? 3
    },
    {
      reason: 'outside-company',
      title: oups?.outsideCompanyTitle || 'oups.outsideCompanyTitle',
      content: oups?.outsideCompanyText || 'oups.outsideCompanyText',
      backto: backTo[previous.page] ?? 3
    },
    {
      reason: 'activity',
      title: oups?.activityTitle || 'oups.activityTitle',
      content: oups?.activityText || 'oups.activityText',
      backto: backTo[previous.page] ?? 4
    },
    {
      reason: 'origin',
      title: oups?.originTitle || 'oups.originTitle',
      content: oups?.originText || 'oups.originText',
      backto: backTo[previous.page] ?? 5
    },
    {
      reason: 'low-turnover',
      title: oups?.lowTurnoverTitle || 'oups.lowTurnoverTitle',
      content: oups?.lowTurnoverText || 'oups.lowTurnoverText',
      backto: backTo[previous.page] ?? 5
    },
    {
      reason: 'high-turnover',
      title: oups?.highTurnoverTitle || 'oups.highTurnoverTitle',
      content: oups?.highTurnoverText || 'oups.highTurnoverText',
      backto: backTo[previous.page] ?? 5
    },
    {
      reason: 'registered-email',
      title: oups?.registeredEmailTitle || 'oups.registeredEmailTitle',
      content: oups?.registeredEmailText || 'oups.registeredEmailText',
      backto: backTo[previous.page] ?? 6
    },
    {
      reason: '401',
      title: oups?.genericTitle || 'oups.genericTitle',
      content: oups?.genericText || 'oups.genericText',
      backto: 0
    },
  ]
  const item = reasons.find( elm => elm.reason === reason )
  const contentPart = item.content.split('{{mail}}')

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(oups?.mail || 'oups.mail');
    } catch (err) {
      console.error("Failed to copy :", err.message);
    }
  };

  // Came from first page and redirected less than twice
  if (page === 'index' && issues.length < 2){
    return <Navigate
      to={(project === 'rc-pro' ? process.env.REACT_APP_RCPRO_URL : process.env.REACT_APP_RCA_URL)}
      state={{ ...state, issues }}
      replace
    />
  }

  return !item
    ? <Navigate to={(project === 'rc-pro' ? process.env.REACT_APP_RCPRO_URL : process.env.REACT_APP_RCA_URL)} replace />
    : <div className={style.content}>
      <h1>{item.title} 👀</h1>
      <p>
        {contentPart[0]}
        {contentPart.length > 1 && <>
          <span className={style.mail} onClick={copyToClipboard}>{oups?.mail || 'oups.mail'}<Copy /></span>
          {contentPart[1]}
        </>}
      </p>

      {issues.length < 2 &&
        <Link
          className={style.link}
          label={
            <PrefixedText
              prefix={<Edit />}
              text={reason === '401' ? "Revenir à l'étape précédente" : oups?.button}
            />
          }
          onClick={() => navigate(reason === '401' ? '../'+page : '../'+pages[item.backto], { state: { ...state, issues }})}
          state={state}
        />
      }
    </div>
}

export default RCProOups